import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Select, Space, App, Popconfirm, Typography, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, BellOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  OrderNotificationAssignmentDto,
  assignOrderNotificationRecipient,
  fetchOrderNotificationAssignments,
  removeOrderNotificationRecipient,
} from '../modules/orders/api/orderNotificationsApi';
import { useNotificationRecipientOptions } from '../modules/reference-data/queries/useNotificationRecipientOptions';

const { Text } = Typography;

interface OrderNotificationAssignmentsProps {
  orderId: string;
}

const OrderNotificationAssignments: React.FC<OrderNotificationAssignmentsProps> = ({ orderId }) => {
  const { t } = useTranslation(['notifications', 'common']);
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<OrderNotificationAssignmentDto[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { options: recipients } = useNotificationRecipientOptions();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchData = async () => {
    try {
      setAssignments(await fetchOrderNotificationAssignments(orderId));
    } catch (error) {
      message.error(t('notifications:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedUserId) return;

    setAdding(true);
    try {
      await assignOrderNotificationRecipient(orderId, selectedUserId);
      message.success(t('common:success'));
      setSelectedUserId(null);
      await fetchData();
    } catch (error) {
      message.error(t('notifications:errors.assignFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await removeOrderNotificationRecipient(orderId, userId);
      message.success(t('common:success'));
      await fetchData();
    } catch (error) {
      message.error(t('notifications:errors.removeFailed'));
    }
  };

  // Filter out already assigned users
  const assignedUserIds = new Set(assignments.map(a => a.userId));
  const availableUsers = recipients.filter((user) => !assignedUserIds.has(user.id));

  const columns = [
    {
      title: t('common:name'),
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: t('common:email'),
      dataIndex: 'userEmail',
      key: 'userEmail',
    },
    {
      title: t('notifications:assignments.assignedBy'),
      dataIndex: 'assignedByName',
      key: 'assignedByName',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: OrderNotificationAssignmentDto) => {
        if (record.source === 'ACCOUNT_MANAGER') {
          return null;
        }

        return (
          <Popconfirm
            title={t('notifications:assignments.confirmRemove')}
            onConfirm={() => { void handleRemove(record.userId); }}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <Space>
          <BellOutlined />
          {t('notifications:assignments.title')}
        </Space>
      }
      size="small"
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {t('notifications:assignments.description')}
      </Text>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 10,
          alignItems: 'center',
          marginBottom: 16,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Select
          style={{ width: '100%', minWidth: 0 }}
          placeholder={t('notifications:assignments.selectUser')}
          value={selectedUserId}
          onChange={setSelectedUserId}
          options={availableUsers.map(u => ({
            value: u.id,
            label: `${u.name} (${u.email})`
          }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string).toLowerCase().includes(input.toLowerCase())
          }
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={adding}
          disabled={!selectedUserId}
          style={{ minWidth: 0 }}
        >
          {t('notifications:assignments.addUser')}
        </Button>
      </div>

      {assignments.length > 0 ? (
        <Table
          dataSource={assignments}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 520 }}
        />
      ) : (
        <Empty
          description={t('notifications:assignments.noAssignments')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Card>
  );
};

export default OrderNotificationAssignments;
